// Third party
import React, { useContext, useEffect, useState } from "react";

import { CircularProgress, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";

// Custom
import { AlignCenter, Fade, Modal, StyledButton } from "../components";
import Star from "../components/icons/Star";
import LeaveQuizButton from "../components/LeaveQuizButton";
import WobblyTypography from "../components/WobblyTypography";
import { green, purple, red } from "../config";
import { dagnyBlackFont } from "../config/theme";
import { LangContext } from "../contexts/langContext";
import { TestStoreContext } from "../contexts/testStoreContext";
import { IPlayer, WebsocketContext } from "../contexts/websocketContext";
import BackgroundOrbs from "../features/BackgroundOrbs";
import LeaderBoard from "../features/multiplayer/LeaderBoard";
import RenderQuestion from "../features/RenderQuestion";
import useQuizSlideInfo from "../hooks/useQuizSlideInfo";
import useSocket from "../hooks/useWebsocket";
import { AnswerStat } from "../interfaces/answerStats.interface";
import { QuizSlideDataSequence, SlideData } from "../interfaces/question.interface";
import AnswerFeedback from "../slides/AnswerFeedback";
import { slideDataIsQuestion } from "../util/questionUtils";
import { SlideDataContext } from "@natklok/contexts/slideDataContext";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const MultiplayerResultPage: React.FC = () => {
  const { isMultiplayerHost } = useContext(WebsocketContext);
  const { setConfettiIsRunning } = useContext(TestStoreContext);
  const theme = useTheme();
  const normalBottomGrid = useMediaQuery("(max-width:1110px) and (min-width:880px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  useEffect(() => {
    setConfettiIsRunning(true);
  }, [setConfettiIsRunning]);

  return (
    <div>
      <div
        style={{
          width: "100%",
          textAlign: "center",
          margin: "-30px 0 10px 0",
          pointerEvents: "none",
        }}
      >
        <WobblyTypography variant="h4" marginTop={12} component="h1">
          GAME OVER!
        </WobblyTypography>
      </div>
      <div>
        <Fade>
          <BackgroundOrbs style={{ height: "60vh", marginTop: 0 }} resultBlobs />
        </Fade>
        {isMobile ? (
          <AlignCenter withMaxWidth style={{ textAlign: "center", marginTop: 22 }}>
            <LeaderBoard />
          </AlignCenter>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
              margin: "0 30px",
            }}
          >
            <Podium
              place={2}
              style={{
                height: 240,
                boxShadow: "none",
                marginRight: -10,
                borderBottomLeftRadius: 20,
              }}
            />
            <Podium place={1} />
            <Podium
              place={3}
              style={{
                height: 200,
                boxShadow: "none",
                marginLeft: -10,
                borderBottomRightRadius: 20,
              }}
            />
          </div>
        )}
      </div>
      <Fade delay={2500}>
        <Grid container alignItems={"flex-start"} justifyContent="center" style={{ marginTop: 30 }}>
          <TotalScore />
          <HardQuestions />
          {isMultiplayerHost && !normalBottomGrid && <HostResultQuizControls />}
        </Grid>
      </Fade>
    </div>
  );
};

const HostResultQuizControls = () => {
  const { wsRestartGame } = useSocket();
  const translatePageText = useTranslatePageText();

  return (
    <Grid
      item
      style={{
        display: "flex",
        flexDirection: "column",
        width: 250,
        background: "rgba(0,0,0,0.2)",
        padding: 16,
        borderRadius: 8,
        margin: 10,
      }}
    >
      <div>
        <Typography variant="body1" style={{ marginBottom: 4 }}>
          {translatePageText("forTeachers.redoQuizWithSamePlayers")}
        </Typography>
        <StyledButton
          label={translatePageText("forTeachers.doAnotherQuiz")}
          onClick={() => wsRestartGame()}
          style={{ width: "100%", marginTop: 10 }}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <LeaveQuizButton size="medium" fullWidth endGame style={{ margin: 0 }} />
      </div>
    </Grid>
  );
};

const Podium: React.FC<{ place: number; style?: React.CSSProperties }> = ({
  place,
  style = {},
}) => {
  const { leaderboard, players } = useContext(WebsocketContext);
  const { questionAmount } = useQuizSlideInfo();

  let score = 0;
  const names: Array<string> = [];
  const bgd = ["#ffac33", "#b5bdd2", "#e07c48"][place - 1];
  const brighterbgd = ["#fdd08d", "#dadee8", "#e6a380"][place - 1];
  if (leaderboard.length >= place) {
    score = leaderboard[place - 1].score;
    leaderboard[place - 1].playerIds.forEach((playerId: string) => {
      const player: IPlayer | undefined = players.find((player: IPlayer) => player.id === playerId);
      if (player !== undefined) {
        names.push(player.name);
      }
    });
  }

  return (
    <div style={{ zIndex: 3 - place, width: "100%", maxWidth: 220 }}>
      <Fade delay={(3 - place) * 500}>
        <div
          style={{
            maxWidth: place === 1 ? 240 : 220,
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <PodiumDynamicText nameArray={names} />
          <div
            style={{
              background: bgd,
              width: "100%",
              height: 300,
              textAlign: "center",
              boxShadow: "0px 0px 10px 5px rgba(0, 9, 95, 0.25)",
              position: "relative",
              ...style,
            }}
          >
            <div
              style={{
                width: "calc(100% + 20px)",
                marginLeft: -10,
                height: place * 5 + 8,
                borderBottom: (place * 5 + 8).toString() + "px solid " + brighterbgd,
                borderRight: "10px solid transparent",
                borderLeft: "10px solid transparent",
              }}
            ></div>
            <div
              style={{
                width: "calc(100% + 20px)",
                marginLeft: -10,
                height: 20,
                background: bgd,
                boxShadow: "0px 10px 10px 5px rgba(0, 0, 0, 0.25)",
              }}
            ></div>
            <p
              style={{
                fontSize: window.innerWidth < 500 ? 60 : 90,
                margin: "-10px 0 0 0",
                fontFamily: dagnyBlackFont,
                color: "black",
                opacity: 0.5,
              }}
            >
              {place}
            </p>
            {names.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: -20,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "rgba(0,0,0,0.15)",
                    padding: "3px 18px",
                    borderRadius: "1000px",
                  }}
                >
                  <Typography color="rgba(0,0,0,0.4)" fontFamily={dagnyBlackFont}>
                    {score}/{questionAmount}
                  </Typography>
                  <Star
                    opacity={1}
                    style={{ marginLeft: 8, transform: `rotate(${Math.random() * 30 - 15}deg)` }}
                    fontSize="24px"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Fade>
    </div>
  );
};

const PodiumDynamicText: React.FC<{ nameArray: Array<string> }> = ({ nameArray }) => {
  let str = nameArray.toString().replace(",", ", ");
  if (str.length > 90) {
    const i = str.slice(0, 90).match(/,/g)?.length || nameArray.length;
    str =
      nameArray.slice(0, i).toString().replace(",", ", ") +
      " och " +
      (nameArray.length - i).toString() +
      " andra";
  }
  const logisticalFontSizeFunc = (c: number) => {
    return 20.2743 / (1 - 0.7166 * Math.pow(Math.E, -0.0269 * c));
  };
  return (
    <Typography
      variant="h5"
      component="span"
      textAlign="center"
      mb={1}
      fontSize={logisticalFontSizeFunc(str.length) + "px !important"}
    >
      {str}
    </Typography>
  );
};

const sortAfterAmountIncorrect = (allSlideData: QuizSlideDataSequence) => {
  const sortedAfterDiff: Array<SlideData> = [];

  allSlideData.forEach((slideData: SlideData) => {
    if (slideDataIsQuestion(slideData)) {
      sortedAfterDiff.push(slideData);
    }
  });
  sortedAfterDiff.sort((a, b) => {
    const percentWrongA = getPercentWrong(a.answerStat);
    const percentWrongB = getPercentWrong(b.answerStat);

    if (a.answerStat && b.answerStat) {
      return percentWrongA - percentWrongB;
    }
    return 1;
  });

  sortedAfterDiff.reverse();

  return sortedAfterDiff;
};

const HardQuestions: React.FC = () => {
  const { quizSlideDataSequence } = useContext(SlideDataContext);
  const [sortedAfterDiff] = useState<Array<SlideData>>(
    sortAfterAmountIncorrect(quizSlideDataSequence)
  );
  const [showAll, setShowAll] = useState(false);
  const translatePageText = useTranslatePageText();

  const hardSlides = showAll ? sortedAfterDiff : sortedAfterDiff.slice(0, 2);

  return (
    <Grid
      item
      style={{
        background: "rgba(0,0,0,0.2)",
        padding: 16,
        borderRadius: 8,
        margin: 10,
        maxWidth: 450,
      }}
    >
      <div style={{ display: "flex", margin: "10px 0px 10px 0" }}>
        <Typography variant="h5" style={{ margin: 0, lineHeight: 1 }}>
          {translatePageText("forTeachers.hardestQuestions")}
        </Typography>
      </div>
      {hardSlides.map((slideData: SlideData) => (
        <HardQuestionContainer questionSlide={slideData} />
      ))}

      <StyledButton
        variant="secondary"
        label={
          showAll
            ? translatePageText("forTeachers.makeSmaller")
            : translatePageText("forTeachers.showAllQuestions")
        }
        style={{ width: "100%", margin: "15px 0 0 0" }}
        onClick={() => setShowAll(!showAll)}
      />
    </Grid>
  );
};

const getPercentWrong = (answerStat: AnswerStat | undefined): number => {
  if (answerStat === undefined) return 0;
  const totaltAnswers =
    answerStat.amountCorrect + answerStat?.amountIncorrect + answerStat?.amountNotAnswered;

  let res = (answerStat.amountIncorrect / totaltAnswers) * 100;
  if (isNaN(res)) {
    res = 0;
  }
  return Math.round(res);
};

const HardQuestionContainer: React.FC<{ questionSlide: SlideData }> = ({ questionSlide }) => {
  const { translateTranslationData } = useContext(LangContext);
  const [modalVisible, setModalVisible] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const percentWrong = getPercentWrong(questionSlide.answerStat);

  if (questionSlide.answerStat === undefined) return null;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.8)",
        borderRadius: 8,
        marginTop: 15,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ padding: 16 }}>
          <Typography
            variant="h6"
            lineHeight={"1.5rem"}
            color={purple[600]}
            style={{ margin: "0 0 8px 0" }}
          >
            {translateTranslationData(questionSlide.question?.title)}
          </Typography>
          <Typography variant="body2" color={purple[500]}>
            {translateTranslationData(questionSlide.question?.desc)}
          </Typography>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 120,
            marginLeft: "1em",
            background: "white",
            padding: "8px",
          }}
        >
          <div
            style={{
              position: "relative",
              height: 44,
              width: 40,
            }}
          >
            <CircularProgress
              style={{ color: green[500], position: "absolute" }}
              variant="determinate"
              value={100}
              thickness={8}
              size={40}
            />
            <CircularProgress
              style={{ color: red[700], position: "absolute" }}
              variant="determinate"
              value={percentWrong}
              thickness={8}
              size={40}
            />
          </div>
          <Typography
            variant="body2"
            textAlign={"center"}
            fontWeight="bold"
            color={percentWrong > 50 ? red[700] : green[500]}
          >
            {percentWrong}% fel
          </Typography>
          <StyledButton onClick={() => setModalVisible(true)} style={{ marginTop: 8 }} size="small">
            Öppna
          </StyledButton>
        </div>
      </div>

      <Modal
        open={modalVisible}
        setOpen={setModalVisible}
        title=""
        style={{ background: purple[500], color: "white", width: "90vw", height: "90vh" }}
      >
        {!showAnswer && (
          <RenderQuestion showCorrectAnswer={true} question={questionSlide.question} />
        )}
        {showAnswer && (
          <AnswerFeedback
            slideData={questionSlide}
            answerDescription={""}
            correctOrIncorrect={"correct"}
            forMultiplayerHost
            showQuestionAgainButton={false}
          />
        )}
        <StyledButton
          variant="secondary"
          style={{ position: "absolute", top: 16, left: 16 }}
          onClick={() => setShowAnswer(!showAnswer)}
        >
          {showAnswer ? "Se frågan" : "Se facit"}
        </StyledButton>
      </Modal>
    </div>
  );
};

const TotalScore: React.FC = () => {
  const { quizSlideDataSequence } = useContext(SlideDataContext);
  const translatePageText = useTranslatePageText();
  const normalBottomGrid = useMediaQuery("(max-width:1110px) and (min-width:880px)");

  let totalCorrectAmount = 0;
  let totalAmount = 0;

  quizSlideDataSequence.forEach((slideData: SlideData) => {
    if (slideData.answerStat !== undefined && slideData.question !== undefined) {
      totalCorrectAmount += slideData.answerStat.amountCorrect;
      totalAmount += slideData.answerStat.amountCorrect;
      totalAmount += slideData.answerStat.amountIncorrect;
      totalAmount += slideData.answerStat.amountNotAnswered;
    }
  });

  const percentCorrect = Math.round((totalCorrectAmount / (totalAmount + 0.001)) * 100);

  return (
    <Grid item>
      <div
        style={{
          background: "rgba(0,0,0,0.2)",
          padding: 16,
          borderRadius: 8,
          margin: 10,
        }}
      >
        <Typography variant="h5" style={{ margin: "10px 0px 20px 0", lineHeight: 1 }}>
          {translatePageText("forTeachers.theClassesResults")}
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            height: 120,
            width: 320,
            marginTop: 16,
          }}
        >
          <div style={{ position: "relative", width: 100 }}>
            <CircularProgress
              style={{ color: red[500], position: "absolute", top: 7, left: 7 }}
              variant="determinate"
              value={100}
              thickness={10}
              size={86}
            />
            <CircularProgress
              style={{ color: green[500], position: "absolute" }}
              variant="determinate"
              value={percentCorrect}
              thickness={12}
              size={100}
            />
          </div>
          <div style={{ marginLeft: 12, marginTop: 10 }}>
            <Typography fontFamily={dagnyBlackFont} variant="h2" lineHeight="0.9">
              {percentCorrect}%
            </Typography>
            <Typography fontWeight="700">
              {translatePageText("test.correctAnswer").toLowerCase()}
            </Typography>
          </div>
        </div>
      </div>
      {normalBottomGrid && (
        <div style={{ float: "right" }}>
          <HostResultQuizControls />
        </div>
      )}
    </Grid>
  );
};

export default MultiplayerResultPage;
